import { useFetchData } from '../../../../hooks';
import { PageableResponse } from '../../../../types';
import { BOM_API } from '../../../../hooks/useBillOfMaterialHooks';
import { Link, useSearchParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { Table, TableColumnsType } from 'antd';
import { BillOfMaterialList } from '../../../../types/BillOfMaterial';
import StatusTag from '../../../../components/ui/StatusTag';
import {
  convertDate,
  dateFormat,
  dateTimeFormat,
  getEnumValue,
} from '../../../../lib/utils';
import ErrorModal from '../../../../components/ui/modal/ErrorModal';
import { RequestStatusEnum, Resource } from '../../../../lib/enums';
import dayjs from 'dayjs';

// Newest-first friendly comparator; missing dates sort as oldest.
const dateSorter = (x?: string | null, y?: string | null) =>
  (x ? dayjs(x).valueOf() : 0) - (y ? dayjs(y).valueOf() : 0);

const BillOfMaterialTable = () => {
  const [searchParams] = useSearchParams();

  // A code search should find a BOM regardless of its shipment date. The list
  // otherwise filters dateshipment to [now-6mo, now+2mo], which hides BOMs that
  // ship further out (the newest ones) and traps searches inside that window.
  const searchTerm = searchParams.get('search') || '';
  const isSearching = searchTerm.trim() !== '';

  const {
    data: data,
    isLoading,
    error,
    isError,
  } = useFetchData<PageableResponse<BillOfMaterialList>>(BOM_API, {
    // MIN, not FULL: the list only shows header columns. FULL pulls every BOM's
    // material details and, over a wide date window, OOMs the API (heap 512m).
    resource: searchParams.get('resource') || Resource.MIN,
    search: searchTerm,
    from:
      searchParams.get('from') ??
      (isSearching
        ? '2000-01-01'
        : dayjs().subtract(6, 'month').format(dateFormat)),
    to:
      searchParams.get('to') ??
      (isSearching
        ? '2999-12-31'
        : dayjs().add(12, 'month').format(dateFormat)),
    status: getEnumValue(RequestStatusEnum, searchParams.get('status') ?? ''),
    length: parseInt(searchParams.get('length') || '999999'),
  });
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMess, setErrorMess] = useState<string | undefined>('');
  useEffect(() => {
    if (isError) {
      setIsErrorModalOpen(isError);
      setErrorMess(error?.response?.data.message);
    }
  }, [isError]);

  const handleErrorModalClose = () => {
    setIsErrorModalOpen(false);
  };

  const columns: TableColumnsType<BillOfMaterialList> = [
    {
      title: 'BOM',
      dataIndex: 'bom',
      width: 120,
      render: (_text, record) => (
        <Link to={`/production/bill-of-material/${record.id}?action=view`}>
          <span className={'text-blue-400 font-semibold'}>
            {record.code || record.id}
          </span>
        </Link>
      ),
    },
    {
      title: 'Purchase Order',
      dataIndex: 'pono',
    },
    {
      title: 'SKU',
      dataIndex: 'sku',
    },
    {
      title: 'Shipment Date',
      dataIndex: 'dateshipment',
      sorter: (a, b) => dateSorter(a.dateshipment, b.dateshipment),
    },
    {
      title: 'Required Date',
      dataIndex: 'requiredDate',
      sorter: (a, b) => dateSorter(a.requiredDate, b.requiredDate),
    },
    {
      title: 'Date Entry',
      dataIndex: 'dateEntry',
      sorter: (a, b) => dateSorter(a.dateEntry, b.dateEntry),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Buyer Code',
      dataIndex: 'buyercode',
    },
    {
      title: 'Revision',
      dataIndex: 'revision',
    },
    {
      title: 'Status',
      dataIndex: 'buyercode',
      width: 80,
      render: (_text, record) => (
        <StatusTag status={record.status.toUpperCase().replace(' ', '_')} />
      ),
    },
    {
      title: 'Prepared By',
      dataIndex: 'preparedBy',
    },
    {
      title: 'Created By/Date',
      dataIndex: 'createdBy',
      sorter: (a, b) => dateSorter(a.timeCreated, b.timeCreated),
      render: (createdBy, record) => (
        <div className="flex flex-col">
          <span className="mr-2">{createdBy}</span>
          {record.timeCreated != null && (
            <span>{convertDate(record.timeCreated, dateTimeFormat)}</span>
          )}
        </div>
      ),
    },
    {
      title: 'Updated By/Date',
      dataIndex: 'updatedBy',
      render: (updatedBy, record) => (
        <div className="flex flex-col">
          <span className="mr-2">{updatedBy}</span>
          {record.timeUpdated != null && (
            <span>{convertDate(record.timeUpdated, dateTimeFormat)}</span>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <div className={'flex flex-col h-[calc(79vh)] overflow-auto'}>
        <Table
          rowKey={(record) => record.id}
          dataSource={data?.data}
          loading={isLoading}
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          columns={columns}
        />
      </div>
      <ErrorModal
        visible={isErrorModalOpen}
        message={errorMess}
        onClose={handleErrorModalClose}
      />
    </>
  );
};

export default BillOfMaterialTable;
